/* eslint-disable max-lines */
import { useContext, useEffect } from 'react';
import useLocale from './use-locale';
import {
  formatPricePixel,
  formatPriceTagManager,
  getProduct,
  setPixel,
  setTagManager,
  trackPixelEvent,
  trackTagManagerEvent,
  trackTikTokPixelEvent,
} from '../Utils/tracking';
import useGiftcardPrices from './use-giftcard-prices';
import useGiftcardShop from '../Pages/Giftcard/server-data/use-giftcard-shop';
import useGiftcardMinimalShop from '../Pages/Giftcard/server-data/use-giftcard-minimal-shop';
import { giftcardState } from '../Pages/Giftcard/client-state/giftcard-state-context';
import { STAPE_GTM_ID } from '../config';

const useGiftcardTracking = () => {
  const { data: minimalShop } = useGiftcardMinimalShop();
  const { data: shop } = useGiftcardShop();
  const { state } = useContext(giftcardState);
  const { language } = useLocale();
  const { giftcardPrices } = useGiftcardPrices();

  // When shop changes, init GTM and FB Pixel
  useEffect(() => {
    if (shop) {
      if (process.env.REACT_APP_ANALYTICS_ENABLED === 'true') {
        if (shop?.configuration?.gtmId && shop.configuration.gtmId !== STAPE_GTM_ID) {
          setTagManager(shop?.configuration?.gtmId, language);
        }
        if (shop?.configuration?.pixelId) {
          setPixel(shop?.configuration?.pixelId);
        }
      } else {
        console.log(
          `GTM ID: ${shop?.configuration?.gtmId}, Pixel ID: ${shop?.configuration?.pixelId}`
        );
      }
    }
  }, [shop, language]);

  const shopId = shop?.id;
  const shopName = shop?.name;

  // Listen for events to be tracked
  const track = (action: {
    type: string;
    product?: any;
    step?: any;
    paymentType?: any;
    subscribed?: any;
    duration?: any;
    boatCount?: any;
  }) => {
    try {
      switch (action.type) {
        case 'PRODUCT_VIEW_GIFTCARD': {
          const product = getProduct({
            type: 'giftcard',
            duration: action.product.duration,
          });

          const event = {
            event: 'productView',
            ecommerce: {
              detail: {
                actionField: { step: '1' },
                products: [
                  {
                    name: product.name,
                    id: product.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(giftcardPrices.fullPrice ?? 0),
                    category: 'Giftcard',
                    quantity: 1,
                  },
                ],
              },
            },
          };

          trackTikTokPixelEvent('ViewContent');
          trackPixelEvent('ViewContent', undefined);
          return trackTagManagerEvent(event);
        }

        case 'ADD_TO_CART_GIFTCARD': {
          const product = getProduct({
            type: 'giftcard',
            duration: action.product.duration,
          });

          const event = {
            event: 'addToCart',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              add: {
                products: [
                  {
                    name: product.name,
                    id: product.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(giftcardPrices.fullPrice ?? 0),
                    category: 'Giftcard',
                    quantity: 1,
                  },
                ],
              },
            },
          };

          const pixelObject = {
            value: formatPricePixel(giftcardPrices.fullPrice ?? 0),
            currency: minimalShop?.currency,
            content_name: product.name,
            content_type: 'product',
            contents: [
              {
                id: product.id,
                quantity: 1,
                item_price: formatPricePixel(giftcardPrices.fullPrice ?? 0),
              },
            ],
          };

          const tikTokPixelData = {
            content_type: 'product',
            quantity: 1,
            description: 'Giftcard',
            content_id: product.id,
            currency: minimalShop?.currency,
            value: formatPricePixel(giftcardPrices.fullPrice ?? 0),
          };

          trackTikTokPixelEvent('AddToCard', tikTokPixelData);
          trackPixelEvent('AddToCart', pixelObject);
          return trackTagManagerEvent(event);
        }

        case 'CHECKOUT_GIFTCARD': {
          const product = getProduct({
            type: 'giftcard',
            duration: action.product.duration,
          });

          const event = {
            event: 'checkout',
            ecommerce: {
              checkout: {
                actionField: action.paymentType
                  ? { step: action.step }
                  : { step: action.step, option: action.paymentType },
                products: [
                  {
                    name: product.name,
                    id: product.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(giftcardPrices.fullPrice ?? 0),
                    category: 'Giftcard',
                    quantity: 1,
                  },
                ],
              },
            },
          };

          if (action.step === 1) {
            trackPixelEvent('InitiateCheckout', undefined);
            trackTikTokPixelEvent('InitiateCheckout');
          }
          if (action.step === 5) {
            trackPixelEvent('AddPaymentInfo', undefined);
            trackTikTokPixelEvent('AddPaymentInfo');
          }

          return trackTagManagerEvent(event);
        }

        case 'TRANSACTION_COMPLETE_GIFTCARD': {
          const product = getProduct({
            type: 'giftcard',
            duration: action.product.duration,
          });

          const event: any = {
            event: 'transactionComplete',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              purchase: {
                actionField:
                  action.product.campaigns.length > 0
                    ? {
                        id: action.product.id,
                        revenue: formatPriceTagManager(giftcardPrices.fullPrice ?? 0),
                        coupon: action.product.campaigns[0].code,
                      }
                    : {
                        id: action.product.id,
                        revenue: formatPriceTagManager(giftcardPrices.fullPrice ?? 0),
                      },
                products: [
                  {
                    name: product.name,
                    id: product.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(giftcardPrices.fullPrice ?? 0),
                    category: 'Giftcard',
                    quantity: 1,
                  },
                ],
              },
            },
          };

          // add user data to GTM event
          if (
            state.customer.customer.firstname &&
            state.customer.customer.lastname &&
            state.customer.customer.email &&
            state.customer.customer.phone
          ) {
            event.user_data = {
              email: state.customer.customer.email.toLowerCase(),
              phone_number: state.customer.customer.phone,
              address: [
                {
                  first_name: state.customer.customer.firstname.toLowerCase(),
                  last_name: state.customer.customer.lastname.toLowerCase(),
                },
              ],
            };

            if (state.customer.customer.address?.country) {
              event.user_data.address[0].country =
                state.customer.customer.address?.country.toLowerCase();
            }
          }

          const pixelObject = {
            value: formatPricePixel(giftcardPrices.fullPrice ?? 0),
            currency: minimalShop?.currency,
            content_name: product.name,
            content_type: 'product',
            contents: [
              {
                id: product.id,
                quantity: 1,
                item_price: formatPricePixel(giftcardPrices.fullPrice ?? 0),
              },
            ],
          };

          const tikTokPixelObject = {
            content_type: 'product',
            quantity: 1,
            description: 'Gift card',
            content_id: product.id,
            currency: minimalShop?.currency,
            value: formatPricePixel(giftcardPrices.fullPrice ?? 0),
          };

          trackTikTokPixelEvent('CompletePayment', tikTokPixelObject);
          trackPixelEvent('Purchase', pixelObject);
          return trackTagManagerEvent(event);
        }

        case 'SUBSCRIBE_TO_NEWSLETTER': {
          const event = {
            event: 'subscribedToNewsletter',
            subscribed: action.subscribed,
          };

          trackTikTokPixelEvent('Subscribe');
          return trackTagManagerEvent(event);
        }

        case 'CHANGE_DURATION': {
          const event = {
            event: 'changeDuration',
            newDuration: action.duration,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_BOAT_COUNT': {
          const event = {
            event: 'changeBoatCount',
            newBoatCount: action.boatCount,
          };

          return trackTagManagerEvent(event);
        }

        default: {
          // return error, unvalid action.type
          return null;
        }
      }
    } catch (error) {
      console.error('tracking error', error);
    }
  };

  return track;
};

export default useGiftcardTracking;
